import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

import { persistStore, persistReducer, createTransform } from "redux-persist";
import { combineReducers } from "@reduxjs/toolkit";

import storage from "redux-persist/lib/storage";

import { CONFIG } from "../common/constant/constant";
import userReducer from "./slice/user.slice";
import toastReducer from "./slice/toast.slice";
import activeIndexSlice from "./slice/activeIndex";
import { loginApi } from "./services/loginApi";
import { dashboardApi } from "./services/dashboardApi";
import { projectApi } from "./services/projectApi";
import { settingsApi } from "./services/settingsApi";
import { venderApi } from "./services/venderApi";
import { coinsApi } from "./services/coinsApi";
import { thirdPartyKeysApi } from "./services/thirdPartyKeysApi";

import commonLoadingSlice from "./slice/commonLoading.slice";
import { addCmcKeyApi } from "./services/cmcApi";
import addcmcSlice from "./slice/cms.slice";
import { plansListApi } from "./services/plansListApi";
import { addTokenAPi } from "./services/addTokenApi";
import plansListSlice from "./slice/plansList.slice";
import addTokenSlice from "./slice/addToken.slice";
import { alertsApi } from "./services/alertsApi";
import { baseApi } from "./services/baseApi";
import { cacheApi } from "./services/cacheApi";

/**
 * Older versions persisted the admin token inside the user object in localStorage. Drop it on the
 * way in and out so a leftover token is never read back or written again.
 */
const withoutToken = (userState) => {
  if (!userState?.user?.token) return userState;
  const { token, ...user } = userState.user;
  return { ...userState, user };
};

const persistConfig = {
  key: "root",
  storage: storage,
  transforms: [createTransform(withoutToken, withoutToken, { whitelist: ["userState"] })],
};

/**
 * The session cookie is HttpOnly, so scripts cannot delete it. On logout, ask the server to clear it.
 * Fire and forget: the dashboard is signed out locally either way.
 */
const serverLogoutMiddleware = () => (next) => (action) => {
  if (action.type === "userSlice/logout") {
    fetch(`${CONFIG.ADMIN_API_URL}auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
  }
  return next(action);
};

const combinedReducer = combineReducers({
  [baseApi.reducerPath]: baseApi.reducer,
  [loginApi.reducerPath]: loginApi.reducer,
  [dashboardApi.reducerPath]: dashboardApi.reducer,
  [projectApi.reducerPath]: projectApi.reducer,
  [coinsApi.reducerPath]: coinsApi.reducer,
  [settingsApi.reducerPath]: settingsApi.reducer,
  [venderApi.reducerPath]: venderApi.reducer,
  [thirdPartyKeysApi.reducerPath]: thirdPartyKeysApi.reducer,
  
  [addCmcKeyApi.reducerPath]: addCmcKeyApi.reducer,
  [addTokenAPi.reducerPath]: addTokenAPi.reducer,
      [plansListApi.reducerPath]: plansListApi.reducer,
      [alertsApi.reducerPath]: alertsApi.reducer,
      [cacheApi.reducerPath]: cacheApi.reducer,
  
  toastState: toastReducer,
  userState: userReducer,
  activeIndex: activeIndexSlice,
  loading: commonLoadingSlice,
  addcmcSlice: addcmcSlice,
  plansList: plansListSlice,
  addTokenSlice: addTokenSlice,
});

export const rootReducers = (state, action) => {
  if (action.type === "userSlice/logout") {
    state = undefined;
  }
  return combinedReducer(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducers);
const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false, // {ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],},
    }).concat(
      serverLogoutMiddleware,
      baseApi.middleware,
      loginApi.middleware,
      dashboardApi.middleware,
      projectApi.middleware,
      coinsApi.middleware,
      settingsApi.middleware,
      venderApi.middleware,
      thirdPartyKeysApi.middleware,
      
      addCmcKeyApi.middleware,
      addTokenAPi.middleware,
      plansListApi.middleware,
      alertsApi.middleware,
      cacheApi.middleware
    ),
});

setupListeners(store.dispatch);
let persistor = persistStore(store);
export { store, persistor };
